import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { CollapsedSideBar } from '../collapsedSideBar';
import {
    alert,
    AlertType,
    Container,
    InformationModal,
    NoInteractionView,
} from '@quyva/shared-ui';
import {
    faClipboardList,
    faFileLines,
    faFolder,
    faSave,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Navbar from '../navbar';
import ReportLayoutsDesignerView from './reportLayoutsDesignerView';
import ReportLayoutsSideView from './reportLayoutsSideView';
import FormsSideView from './forms/formsSideView';
import FormDesigner from './forms/formDesigner';
import BeforeSelectView from './beforeSelectView';
import NoTemplateView from './noTemplateView';
import TemplateManager from '../extractionTemplateDesigner/templateManager';
import TransactionList from '../Transactionlist';
import { useGlobalCredits } from '../../context/GlobalStateContext';
import { getUrl } from '../pdfDisplay/utils';
import isEqual from 'lodash.isequal';
import { CategorizationModal } from '../categorizationModal';
import axios from 'axios';
import { useNavigate } from 'react-router';
const minWindowSize = 50;
export const ReportLayoutsView = ({
    setActiveTemplate,
    templateList,
    setTemplateList,
    fetchTemplateList,
    newNotification,
    setNewNotification,
    viewNotifications,
    setViewNotifications,
    messages,
    socket,
    isSideViewExpanded,
    setIsSideViewExpanded,
    openWithTemplateDesigner,
    setOpenWithTemplateDesigner,
    selectedTemplate,
    setSelectedTemplate,
    selectedLayout,
    setSelectedLayout,
    addNewTemplateMode,
    setAddNewTemplateMode,
    isCategorizationTemplateMode,
    setIsCategorizationTemplateMode,
    isCategorizationTemplate,
    setIsCategorizationTemplate,
    templateData,
    setTemplateData,
    retrievedTemplateData,
    setRetrievedTemplateData,
    templateIsSuccessful,
    setTemplateIsSuccessful,
    planName,
}) => {
    const user = JSON.parse(localStorage.getItem('current'));
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingSidebar, setIsLoadingSidebar] = useState(false);
    const [isLoadingSidebarText, setIsLoadingSidebarText] = useState('');
    const { transactionMode, setTransactionMode } = useGlobalCredits();
    const [isMobile, setIsMobile] = useState(false);
    const [selectedTemplateNew, setSelectedTemplateNew] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    const [templateSheetName, setTemplateSheetName] = useState('');
    const navigate = useNavigate();

    // Forms feature state (self-contained — no App.jsx wiring needed).
    const [sidebarMode, setSidebarMode] = useState('templates'); // 'templates' | 'forms'
    const [formMode, setFormMode] = useState(false); // designer open?
    const [activeFormRecord, setActiveFormRecord] = useState(null); // null = new form
    const [formName, setFormName] = useState('');
    const [formConfig, setFormConfig] = useState('');
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [formsRefreshKey, setFormsRefreshKey] = useState(0);

    const openNewForm = () => {
        setActiveFormRecord(null);
        setFormMode(true);
    };

    const openExistingForm = (formRecord) => {
        setActiveFormRecord(formRecord);
        setFormMode(true);
    };

    const handleSaveForm = async () => {
        if (!formName) {
            alert(AlertType.ERROR, 'Form name cannot be empty.');
            return;
        }
        const baseUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/forms`
        );
        try {
            setIsLoading(true);
            let response;
            if (activeFormRecord) {
                response = await axios.put(`${baseUrl}/update`, {
                    email: user.email,
                    form_id: activeFormRecord.form_id,
                    form_name: formName,
                    config: formConfig,
                });
            } else {
                response = await axios.post(`${baseUrl}/create`, {
                    email: user.email,
                    form_name: formName,
                    config: formConfig,
                });
            }
            setActiveFormRecord(response.data?.form);
            setFormsRefreshKey((prev) => prev + 1);
            alert(AlertType.SUCCESS, 'Form saved successfully.');
        } catch (error) {
            alert(
                AlertType.ERROR,
                error.response?.data?.detail || 'Unable to save form.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const canSaveForm = isFormDirty && !!formName.trim();
    const formActions = [
        {
            text: 'Save',
            icon: faSave,
            action: handleSaveForm,
            hoverText: canSaveForm
                ? 'Save the form'
                : 'Make a change to the form to enable saving',
            type: 'success',
            disabled: !canSaveForm,
        },
    ];

    useEffect(() => {
        if (openWithTemplateDesigner) {
            setIsCategorizationTemplateMode(true);
            setAddNewTemplateMode(true);
            setOpenWithTemplateDesigner(false); // Set it to false after it's been processed
        }
    }, [
        openWithTemplateDesigner,
        setIsCategorizationTemplateMode,
        setAddNewTemplateMode,
        setOpenWithTemplateDesigner,
    ]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024); // Change 1024 to your desired breakpoint
        };

        handleResize(); // Initial check
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (isMobile) {
            setAddNewTemplateMode(false);
        }
    }, [isMobile]);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            await fetchTemplateList(user, setActiveTemplate, setTemplateList);
            setIsLoading(false);
        })();
        return () => {};
    }, []);

    if (isLoading) {
        return <NoInteractionView viewText={'Loading ...'} />;
    }

    // Function to clear templateData and load a new template from the API
    const handleLoadTemplate = async (email, templateName) => {
        try {
            setTemplateData(null); // Clear templateData before loading new data
            const response = await fetch(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/read_template?email=${email}&template_name=${templateName}`
                ),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setRetrievedTemplateData(data); // Store the response in templateData
                // console.log('Template data loaded successfully:', data);
                const dynamicKey = Object.keys(data)[0];
                setIsCategorizationTemplate(data[dynamicKey].is_categorization);
            } else {
                alert(AlertType.ERROR, 'Failed to load the template.');
                // console.error('Failed to load the template.');
            }
        } catch (error) {
            alert(AlertType.ERROR, error.message);
            console.error(
                'An error occurred while loading the template:',
                error
            );
        }
    };
    const deepEqual = (obj1, obj2) => {
        console.log('OBJ1:', obj1);
        console.log('OBJ2:', obj2);
        return isEqual(obj1, obj2);
    };

    const handleSaveTemplate = async () => {
        if (
            !selectedTemplateNew ||
            !templateDescription ||
            !templateSheetName
        ) {
            // Check which field(s) are empty and show respective alert messages
            if (!selectedTemplateNew) {
                alert(
                    AlertType.ERROR,
                    'Selected Template name cannot be empty.'
                );
            } else if (!templateDescription) {
                alert(AlertType.ERROR, 'Template Description cannot be empty.');
            } else if (!templateSheetName) {
                alert(
                    AlertType.ERROR,
                    'Template collection name cannot be empty.'
                );
            }
            return; // Exit the function if any field is invalid
        }
        if (!templateData) {
            alert(AlertType.Error, 'No template data to save.');
            return;
        }

        // Parse the templateData to extract the last key-value pair
        const parsedData = JSON.parse(templateData);
        const lastKey = Object.keys(parsedData).pop();

        const lastTemplateData = { [lastKey]: parsedData[lastKey] };

        // Check if template_description and template_data are empty
        if (
            !lastTemplateData[lastKey].template_description ||
            lastTemplateData[lastKey].template_data.length === 0
        ) {
            alert(
                AlertType.ERROR,
                'Template description and data are required.'
            );
            return;
        }
        // Compare templateData with retrievedTemplateData
        if (
            retrievedTemplateData &&
            deepEqual(lastTemplateData, retrievedTemplateData)
        ) {
            alert(
                AlertType.WARNING,
                'Existing Template already exists.Please make some changes in the template to save the edits.'
            );
            return; // Abort saving since there is no difference
        }

        try {
            setIsLoadingSidebar(true);
            const apiUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/generate_csv`
            );
            // Call the Python API running on localhost:8000
            const response = await axios.post(
                apiUrl,
                JSON.stringify(lastTemplateData),
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (response.status === 200) {
                // console.log('CSV generated and saved successfully!');
                await fetchTemplateList(
                    user,
                    setActiveTemplate,
                    setTemplateList
                );
                setTemplateIsSuccessful(true);
            } else {
                console.error('Failed to generate the CSV.');
                alert(AlertType.ERROR, 'Failed to generate template.');
            }
        } catch (error) {
            console.error(
                'An error occurred while generating the CSV:',
                error.response?.data || error.message
            );
            alert(AlertType.ERROR, 'Error in generating template.');
        } finally {
            setIsLoadingSidebar(false);
        }
    };

    const templateActions = [
        {
            text: 'Save',
            icon: faSave,
            action: async () => {
                try {
                    // First, save the template
                    setIsLoading(true);
                    await handleSaveTemplate();
                    // After saving, load the template
                    const templateWithExtension = selectedTemplateNew.endsWith(
                        '.csv'
                    )
                        ? selectedTemplateNew
                        : `${selectedTemplateNew}.csv`;
                    await handleLoadTemplate(user.email, templateWithExtension);
                    setIsLoading(false);
                } catch (error) {
                    // console.error('Error saving or loading template:', error);
                    alert(AlertType.ERROR, 'Unable to save or load template.');
                }
            },
            hoverText: 'Save the template for extraction',
            type: 'success',
        },
    ];

    return (
        <div className='z-0 w-[99vw] h-[99vh] m-auto pt-2'>
            <ReflexContainer orientation='vertical'>
                {!isMobile && (
                    <ReflexElement
                        className='left-pane mr-0.5'
                        minSize={isSideViewExpanded ? 250 : minWindowSize}
                        size={isSideViewExpanded ? 350 : minWindowSize}
                        maxSize={isSideViewExpanded ? 700 : minWindowSize}
                    >
                        <div className='pane-content w-full h-full bg-transparent'>
                            {isSideViewExpanded ? (
                                <div className='w-full h-full border-2 overflow-auto bg-backgrounds-slate rounded-xl p-2 z-10'>
                                    {sidebarMode === 'forms' ? (
                                            <FormsSideView
                                                sidebarMode={sidebarMode}
                                                setSidebarMode={setSidebarMode}
                                                selectedFormId={
                                                    activeFormRecord?.form_id ??
                                                    null
                                                }
                                                onSelectForm={openExistingForm}
                                                onCreateNew={openNewForm}
                                                refreshKey={formsRefreshKey}
                                                setIsRepoViewExpanded={
                                                    setIsSideViewExpanded
                                                }
                                                planName={planName}
                                            />
                                        ) : (
                                            <ReportLayoutsSideView
                                                sidebarMode={sidebarMode}
                                                setSidebarMode={setSidebarMode}
                                                templateList={templateList}
                                                setTemplateList={
                                                    setTemplateList
                                                }
                                                fetchTemplateList={
                                                    fetchTemplateList
                                                }
                                                setActiveTemplate={
                                                    setActiveTemplate
                                                }
                                                selectedTemplate={
                                                    selectedTemplate
                                                }
                                                setSelectedTemplate={
                                                    setSelectedTemplate
                                                }
                                                planName={planName}
                                                selectedLayout={selectedLayout}
                                                setSelectedLayout={
                                                    setSelectedLayout
                                                }
                                                setIsRepoViewExpanded={
                                                    setIsSideViewExpanded
                                                }
                                                setAddNewTemplateMode={
                                                    setAddNewTemplateMode
                                                }
                                                setIsCategorizationTemplate={
                                                    setIsCategorizationTemplate
                                                }
                                                setIsCategorizationTemplateMode={
                                                    setIsCategorizationTemplateMode
                                                }
                                                templateData={templateData}
                                                setTemplateData={
                                                    setTemplateData
                                                }
                                                handleLoadTemplate={
                                                    handleLoadTemplate
                                                }
                                                setRetrievedTemplateData={
                                                    setRetrievedTemplateData
                                                }
                                                isLoadingSidebar={
                                                    isLoadingSidebar
                                                }
                                                setIsLoadingSidebar={
                                                    setIsLoadingSidebar
                                                }
                                                loadingSidebarText={
                                                    isLoadingSidebarText
                                                }
                                                setLoadingSidebarText={
                                                    setIsLoadingSidebarText
                                                }
                                            />
                                        )}
                                </div>
                            ) : (
                                <CollapsedSideBar
                                    icon={
                                        sidebarMode === 'forms'
                                            ? faClipboardList
                                            : faFileLines
                                    }
                                    iconColor={'icons-primary'}
                                    title={
                                        sidebarMode === 'forms'
                                            ? 'Forms'
                                            : 'Templates/Layouts'
                                    }
                                    setIsRepoViewExpanded={
                                        setIsSideViewExpanded
                                    }
                                />
                            )}
                        </div>
                    </ReflexElement>
                )}

                <ReflexSplitter
                    style={{
                        border: 0,
                        background: 'white',
                        width: '6px',
                        zIndex: 10,
                        height: '98vh',
                    }}
                />

                <ReflexElement className='right-pane ml-0.5'>
                    <div className='pane-content w-full h-full bg-transparent'>
                        <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl z-0'>
                            <ReflexContainer orientation='horizontal'>
                                <ReflexElement
                                    className='header'
                                    style={{
                                        minHeight: '4rem',
                                        height: '4rem',
                                        maxHeight: '4rem',
                                    }}
                                >
                                    <div className='pane-content w-full h-full p-2'>
                                        <Navbar
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            templateList={templateList}
                                            selectedTemplate={selectedTemplate}
                                            setSelectedTemplate={
                                                setSelectedTemplate
                                            }
                                            selectedLayout={selectedLayout}
                                            setSelectedLayout={
                                                setSelectedLayout
                                            }
                                            setIsRepoViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                            setTemplateList={setTemplateList}
                                            fetchTemplateList={
                                                fetchTemplateList
                                            }
                                            setActiveTemplate={
                                                setActiveTemplate
                                            }
                                            setAddNewTemplateMode={
                                                setAddNewTemplateMode
                                            }
                                            templateData={templateData}
                                            setTemplateData={setTemplateData}
                                            handleLoadTemplate={
                                                handleLoadTemplate
                                            }
                                            setRetrievedTemplateData={
                                                setRetrievedTemplateData
                                            }
                                        />
                                    </div>
                                </ReflexElement>

                                <ReflexElement flex={1}>
                                    <div className='pane-content h-full w-full p-2'>
                                        {sidebarMode === 'forms' ? (
                                            formMode ? (
                                                <Container
                                                    title='Form Designer'
                                                    icon={faFolder}
                                                    iconColor={'icons-primary'}
                                                    actions={formActions}
                                                >
                                                    <FormDesigner
                                                        activeFormRecord={
                                                            activeFormRecord
                                                        }
                                                        onFormNameChange={
                                                            setFormName
                                                        }
                                                        onFormConfigChange={
                                                            setFormConfig
                                                        }
                                                        onDirtyChange={
                                                            setIsFormDirty
                                                        }
                                                    />
                                                </Container>
                                            ) : (
                                                <div className='w-full h-full flex flex-col justify-center items-center text-texts-secondary300'>
                                                    <FontAwesomeIcon
                                                        icon={faClipboardList}
                                                        className='w-16 h-16 mb-4 drop-shadow text-icons-primary100'
                                                    />
                                                    <div className='text-lg font-semibold'>
                                                        Select a form to edit or
                                                        create a new one
                                                    </div>
                                                </div>
                                            )
                                        ) : transactionMode ? (
                                            <TransactionList />
                                        ) : addNewTemplateMode ? ( // Add this condition
                                            <Container
                                                title='Template Designer'
                                                icon={faFolder}
                                                iconColor={'icons-primary'}
                                                actions={templateActions}
                                            >
                                                <TemplateManager
                                                    selectedTemplate={
                                                        selectedTemplateNew
                                                    }
                                                    setSelectedTemplate={
                                                        setSelectedTemplateNew
                                                    }
                                                    templateDescription={
                                                        templateDescription
                                                    }
                                                    setTemplateDescription={
                                                        setTemplateDescription
                                                    }
                                                    templateSheetName={
                                                        templateSheetName
                                                    }
                                                    setTemplateSheetName={
                                                        setTemplateSheetName
                                                    }
                                                    retrievedTemplateData={
                                                        retrievedTemplateData
                                                    }
                                                    setRetrievedTemplateData={
                                                        setRetrievedTemplateData
                                                    }
                                                    onTemplateDataChange={
                                                        setTemplateData
                                                    }
                                                    templateIsSuccessful={
                                                        templateIsSuccessful
                                                    }
                                                    setTemplateIsSuccessful={
                                                        setTemplateIsSuccessful
                                                    }
                                                    setIsCategorizationTemplate={
                                                        setIsCategorizationTemplate
                                                    }
                                                    isCategorizationTemplate={
                                                        isCategorizationTemplate
                                                    }
                                                    templateList={templateList}
                                                    setIsLoadingSidebar={
                                                        setIsLoadingSidebar
                                                    }
                                                />
                                            </Container>
                                        ) : templateList.length === 0 ? (
                                            <NoTemplateView />
                                        ) : (
                                            // Render TemplateManager if addNewTemplateMode is true
                                            <>
                                                {selectedLayout === null ? (
                                                    <BeforeSelectView />
                                                ) : (
                                                    <ReportLayoutsDesignerView
                                                        user={user}
                                                        selectedLayout={
                                                            selectedLayout
                                                        }
                                                        selectedTemplate={
                                                            selectedTemplate
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </ReflexElement>
                            </ReflexContainer>
                        </div>
                    </div>
                </ReflexElement>
            </ReflexContainer>
            {isCategorizationTemplateMode && (
                <CategorizationModal
                    setIsCategorizationTemplate={setIsCategorizationTemplate}
                    isCategorizationTemplate={isCategorizationTemplate}
                    setIsCategorizationTemplateMode={
                        setIsCategorizationTemplateMode
                    }
                    setAddNewTemplateMode={setAddNewTemplateMode}
                />
            )}

            {templateIsSuccessful && (
                <InformationModal
                    modalText={
                        'Template has been generated successfully. You can now access the template content from the sidebar and in settings or extract data using the same.'
                    }
                    close={'true'}
                    setModalClose={setTemplateIsSuccessful}
                    navigate={navigate}
                />
            )}
        </div>
    );
};
